import type { CaApiClient, Turn } from '../shared/types'

export function friendlyError(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const raw = e instanceof Error ? e.message : String(e)
  const stripped = raw.replace(/^CA API \d+:\s*/, '').trim()
  try {
    const parsed = JSON.parse(stripped)
    return parsed?.message || parsed?.data?.message || parsed?.error || stripped
  } catch {
    return stripped || fallback
  }
}

export function isTransportDrop(e: unknown): boolean {
  if (e instanceof TypeError) {
    return true
  }
  return (
    e instanceof Error &&
    /network|failed to fetch|load failed|body stream|stream ended/i.test(e.message)
  )
}

export async function recoverDroppedStream(
  client: CaApiClient,
  conversationId: string,
  question: string,
  baselineTurns: number,
): Promise<boolean> {
  const deadline = Date.now() + 180_000
  let previous: string | undefined
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 10_000))
    try {
      const turns = await client.getMessages(conversationId)
      const fresh = turns.slice(baselineTurns)
      const asked = fresh.some((t) => t.role === 'user' && t.text === question)
      const answered =
        turns.length > 0 && turns[turns.length - 1].role === 'assistant'
      const snapshot = JSON.stringify(turns)
      if (asked && answered && snapshot === previous) {
        return true
      }
      previous = snapshot
    } catch {
    }
  }
  return false
}

export function countVisibleTurns(turns: Turn[]): number {
  return turns.filter(
    (t) => Boolean(t.text) || (t.tables?.length ?? 0) > 0 || (t.charts?.length ?? 0) > 0,
  ).length
}

export function pendingQuestionIndex(turns: Turn[], question: string, turnsBefore = 0): number {
  for (let i = turns.length - 1; i >= turnsBefore; i--) {
    if (turns[i].role === 'user') {
      return turns[i].text === question ? i : turns.length
    }
  }
  return turns.length
}

function answerKeys(turns: Turn[]): (string | undefined)[] {
  const answered = new Map<string, number>()
  let question: string | undefined
  return turns.map((t) => {
    if (t.role === 'user') {
      question = t.text ?? ''
      return undefined
    }
    if (question === undefined) {
      return undefined
    }
    const occurrence = answered.get(question) ?? 0
    answered.set(question, occurrence + 1)
    const key = `${occurrence}:${question}`
    question = undefined
    return key
  })
}

export function carryOverThoughts(server: Turn[], cached: Turn[]): Turn[] {
  const cachedKeys = answerKeys(cached)
  const thoughtsByAnswer = new Map<string, string[]>()
  cached.forEach((t, i) => {
    const key = cachedKeys[i]
    if (key && t.thoughts?.length) {
      thoughtsByAnswer.set(key, t.thoughts)
    }
  })
  if (!thoughtsByAnswer.size) {
    return server
  }
  const serverKeys = answerKeys(server)
  return server.map((t, i) => {
    const key = serverKeys[i]
    const thoughts = key ? thoughtsByAnswer.get(key) : undefined
    return thoughts && !t.thoughts?.length ? { ...t, thoughts } : t
  })
}

export function withThoughtsForQuestion(
  turns: Turn[],
  question: string,
  turnsBefore: number,
  thoughts: string[],
): Turn[] {
  const asked = pendingQuestionIndex(turns, question, turnsBefore)
  const answer = turns[asked + 1]
  if (!thoughts.length || asked >= turns.length || answer?.role !== 'assistant' || answer.thoughts?.length) {
    return turns
  }
  return turns.map((t, i) => (i === asked + 1 ? { ...t, thoughts } : t))
}
